import React, { ReactNode } from 'react';
import { Box, Card, CardContent, Typography, Button } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlags } from '../../contexts/FeatureFlagContext';

/**
 * BookingGate - Route-level wrapper for booking-related pages.
 *
 * When the `booking_enabled` feature flag is off, renders a centered
 * "Coming Soon" card instead of the wrapped page. When on, renders
 * children normally.
 *
 * Respects `isLoading` from FeatureFlagContext so the gate never
 * flashes during initial flag fetch — children render until the flag
 * resolves, matching the safe-default behavior used elsewhere.
 *
 * @example
 * <Route path="/book" element={
 *   <AppLayout showTabs={false}>
 *     <BookingGate><BookingPage /></BookingGate>
 *   </AppLayout>
 * } />
 */

interface BookingGateProps {
  children: ReactNode;
}

const BookingGate: React.FC<BookingGateProps> = ({ children }) => {
  const navigate = useNavigate();
  const { isEnabled, isLoading } = useFeatureFlags();

  // While flags are loading, render children (avoid flashing the gate).
  // Once loaded, gate strictly: only render children when explicitly enabled.
  if (isLoading) {
    return <>{children}</>;
  }

  const bookingEnabled = isEnabled('booking_enabled') === true;

  if (bookingEnabled) {
    return <>{children}</>;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        px: 2,
        py: 4,
      }}
    >
      <Card
        variant="outlined"
        sx={{
          maxWidth: 440,
          width: '100%',
          borderRadius: 3,
          borderColor: '#E0F2F1',
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: 5, px: 3 }}>
          <AccessTimeIcon sx={{ fontSize: 56, color: '#2D6E6A', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1.5, color: '#00796B' }}>
            Booking is coming soon
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            We're working on bringing live booking for hotels, flights, cars, and activities to Percoro. In the meantime, you can plan trips, upload itineraries, and share with friends.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/trips')}
            sx={{
              bgcolor: '#2D6E6A',
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              '&:hover': { bgcolor: '#00796B' },
            }}
          >
            Go to My Trips
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BookingGate;
